import { t } from 'i18next';
import { PlusCircleFill, StarShape } from '@peacecover/icon';
import { useGetMembersWithLeastPhotoCards } from '@pocapan/shared/domains/members/ui/hooks/useGetMembersWithLeastPhotoCards';
import { useGetLatestPhotoCards } from '@pocapan/shared/domains/photoCards/ui/hooks/useGetLatestPhotoCards';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';
import LatestPhotoCardItem, { LatestPhotoCardItemSkeleton } from './LatestPhotoCardItem';
import MemberWithLeastPhotoCardsItem, {
  MemberWithLeastPhotoCardsItemSkeleton
} from './MemberWithLeastPhotoCardsItem';

const HomeView = () => {
  const { data: members, isLoading: isMembersLoading } = useGetMembersWithLeastPhotoCards();
  const { data: latestPhotoCardsData, isLoading: isLatestPhotoCardsLoading } =
    useGetLatestPhotoCards();
  const latestPhotoCards = latestPhotoCardsData?.pages.flatMap((page) => page.photoCards) ?? [];

  return (
    <div className="flex flex-col px-[24px] gap-[20px] mt-[24px] pb-[150px]">
      <HorizontalScroll className="mx-[-24px] px-[24px]">
        <div className="flex items-center gap-[10px]">
          <GuideBannerCard
            className="bg-[linear-gradient(180deg,#3A3A3A_0%,#090909_100%)]"
            label={t('HomeView.fanheartsNoticeBannerLabel')}
            title={t('HomeView.fanheartsNoticeBannerTitle')}
            onClick={() => openNewTab('/bettie-notice/index.html')}
            rightElement={
              <img
                src="/assets/collections/banner_bettie_pocapan.png"
                alt=""
                aria-hidden={true}
                className="absolute right-[9px] top-[8px] h-[76px] w-[56px] object-contain"
              />
            }
          />
          <GuideBannerCard
            className="bg-[linear-gradient(180deg,#DF93FF_0%,#9D76FF_100%)]"
            label={t('HomeView.guideBannerNewbieLabel')}
            title={t('HomeView.guideBannerNewbieTitle')}
            onClick={() => openNewTab('/poca-trade-guide')}
            rightElement={
              <div className="absolute top-[13px] right-[14px]">
                <img
                  src="/assets/collections/pocapan_guide_icon.png"
                  alt="pocapan guide icon"
                  className="text-[#CD8BFF] mix-blend-overlay h-[25px]"
                />
              </div>
            }
          />
        </div>
      </HorizontalScroll>
      <div>
        <div className="flex items-center justify-between py-[4px]">
          <div className="flex items-center gap-[4px]">
            <StarShape size={18} className="text-text-main-lv2" />
            <p className="text-[15px] font-bold leading-[18px] text-text-main-lv2">
              {t('HomeView.latestUpdatesTitle')}
            </p>
          </div>
        </div>
        <div className="mt-[16px] grid grid-cols-5 gap-x-[7px] gap-y-[10px]">
          {isLatestPhotoCardsLoading
            ? Array.from({ length: 10 }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: 정적 스켈레톤 슬롯
                <LatestPhotoCardItemSkeleton key={i} />
              ))
            : latestPhotoCards.map((photoCard) => (
                <LatestPhotoCardItem key={photoCard.photoCardId} photoCard={photoCard} />
              ))}
        </div>
      </div>
      <div className="mt-[4px]">
        <div className="flex items-center justify-between py-[4px]">
          <div className="flex items-center gap-[4px]">
            <PlusCircleFill size={18} className="text-text-main-lv2" />
            <p className="text-[15px] font-bold leading-[18px] text-text-main-lv2">
              {t('HomeView.waitingForReportsTitle')}
            </p>
          </div>
          <p className="text-[11px] leading-[13px] text-text-main-lv2">
            {t('HomeView.firstCaptorDescription')}
          </p>
        </div>
        <HorizontalScroll className="mt-[8px] py-[8px]">
          <div className="flex items-center gap-[10px]">
            {isMembersLoading
              ? Array.from({ length: 6 }, (_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: 정적 스켈레톤 슬롯
                  <MemberWithLeastPhotoCardsItemSkeleton key={i} />
                ))
              : members?.map((member) => (
                  <MemberWithLeastPhotoCardsItem key={member.memberId} member={member} />
                ))}
          </div>
        </HorizontalScroll>
      </div>
    </div>
  );
};

interface GuideBannerCardProps {
  className: string;
  label: string;
  title: string;
  onClick?: () => void;
  rightElement?: React.ReactNode;
}

const GuideBannerCard = ({
  className,
  label,
  title,
  onClick,
  rightElement = null
}: GuideBannerCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className={`${className} relative h-[92px] pl-[15px] pb-[15px] flex flex-col justify-end items-start gap-[4px] w-[169px] rounded-[18px] text-left shrink-0`}
  >
    <p className="text-[12px] font-medium leading-[14px] text-white">{label}</p>
    <p className="whitespace-pre-line text-[15px] font-semibold leading-[18px] text-white">
      {title}
    </p>
    {rightElement}
  </button>
);

export default HomeView;
